import { useMemo, useState } from "react";

import { Star, StarHalf } from "lucide-react";

import { SanitizedTag } from "../utils";

export const SignatureProfileTabs = ({ profile }: { profile: any }) => {
  const biography: string | undefined = profile?.biography || undefined;
  const artistStatement: string | undefined =
    profile?.artistStatement || undefined;

  const exhibitionHistories: any[] = useMemo(
    () =>
      Array.isArray(profile?.exhibitionHistories)
        ? profile.exhibitionHistories
        : [],
    [profile?.exhibitionHistories]
  );

  const publications: any[] = useMemo(
    () => (Array.isArray(profile?.publications) ? profile.publications : []),
    [profile?.publications]
  );

  const reviews: any[] = useMemo(
    () => (Array.isArray(profile?.reviews) ? profile.reviews : []),
    [profile?.reviews]
  );

  const hasBiography = Boolean(biography && biography.trim().length > 0);
  const hasArtistStatement = Boolean(
    artistStatement && artistStatement.trim().length > 0
  );
  const hasExhibitions = exhibitionHistories.length > 0;
  const hasPublications = publications.length > 0;
  const hasReviews = reviews.length > 0;

  const groupedExhibitions = useMemo(() => {
    if (!hasExhibitions) return [] as { year: number; items: any[] }[];
    const byYear: Record<string, any[]> = {};

    for (const item of exhibitionHistories) {
      const key = String(item?.year ?? "");

      if (!byYear[key]) {
        byYear[key] = [];
      }
      byYear[key]?.push(item);
    }

    return Object.keys(byYear)
      .filter(Boolean)
      .map((y) => ({ year: Number(y), items: byYear[y] || [] }))
      .sort((a, b) => b.year - a.year);
  }, [exhibitionHistories, hasExhibitions]);

  const sortedPublications = useMemo(() => {
    if (!hasPublications) return [] as any[];

    return [...publications].sort((a, b) => {
      const da = a?.publicatedAt ? new Date(a.publicatedAt).getTime() : 0;
      const db = b?.publicatedAt ? new Date(b.publicatedAt).getTime() : 0;

      return db - da;
    });
  }, [publications, hasPublications]);

  const tabs = useMemo(
    () =>
      [
        hasBiography && {
          key: "bio",
          label: "Biography",
          render: () => (
            <div
              role="tabpanel"
              aria-labelledby="tab-bio"
              id="panel-bio"
              tabIndex={0}
              className="ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 animate-fade-in mt-0"
            >
              <h3 className="font-playfair text-2xl font-bold mb-4">
                Biography
              </h3>
              <div className="prose max-w-none">
                <SanitizedTag
                  html={biography}
                  tag="p"
                  className="mb-4 whitespace-pre-line"
                />
              </div>
            </div>
          ),
        },
        hasExhibitions && {
          key: "exhibitions",
          label: "Exhibition History",
          render: () => (
            <div
              role="tabpanel"
              aria-labelledby="tab-exhibitions"
              id="panel-exhibitions"
              tabIndex={0}
              className="ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 animate-fade-in mt-0"
            >
              <h3 className="font-playfair text-2xl font-bold mb-6">
                Exhibition History
              </h3>
              <div className="space-y-8">
                {groupedExhibitions.map(({ year, items }) => (
                  <div key={year} className="flex flex-col md:flex-row">
                    <div className="md:w-1/4 font-bold mb-2 md:mb-0">
                      {year}
                    </div>
                    <div className="md:w-3/4">
                      {(items || []).map((it) => (
                        <div
                          key={
                            it?.id || it?.title || Math.random().toString(36)
                          }
                          className="mb-4"
                        >
                          {it?.title ? (
                            <h4 className="font-bold text-lg">{it.title}</h4>
                          ) : null}
                          {it?.body ? <p>{it.body}</p> : null}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ),
        },
        hasArtistStatement && {
          key: "statement",
          label: "Artist Statement",
          render: () => (
            <div
              role="tabpanel"
              aria-labelledby="tab-statement"
              id="panel-statement"
              tabIndex={0}
              className="ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 animate-fade-in mt-0"
            >
              <h3 className="font-playfair text-2xl font-bold mb-4">
                Artist Statement
              </h3>
              <div className="prose max-w-none border-l-4 border-artist-blue pl-6 py-2 italic">
                <SanitizedTag
                  html={artistStatement}
                  tag="p"
                  className="mb-4 whitespace-pre-line"
                />
              </div>
            </div>
          ),
        },
        hasPublications && {
          key: "press",
          label: "Press & Publications",
          render: () => (
            <div
              role="tabpanel"
              aria-labelledby="tab-press"
              id="panel-press"
              tabIndex={0}
              className="ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 animate-fade-in mt-0"
            >
              <h3 className="font-playfair text-2xl font-bold mb-6">
                Press &amp; Publications
              </h3>
              <div className="space-y-6">
                {sortedPublications.map((pub) => (
                  <div
                    key={pub?.id}
                    className="flex flex-col md:flex-row items-start bg-gray-50 p-4 rounded-lg"
                  >
                    <div className="mb-4 md:mb-0">
                      {pub?.pictureLargeUrl ? (
                        <img
                          src={pub.pictureLargeUrl}
                          alt={pub?.title || "Publication"}
                          className="object-contain rounded pr-4"
                          style={{ maxHeight: 150 }}
                        />
                      ) : null}
                    </div>
                    <div className="md:w-3/4">
                      {pub?.title ? (
                        <h4 className="font-bold text-lg">{pub.title}</h4>
                      ) : null}
                      {pub?.publicatedAt ? (
                        <p className="text-sm text-muted-foreground mb-2">
                          {new Date(pub.publicatedAt).toLocaleDateString(
                            undefined,
                            { year: "numeric", month: "long" }
                          )}
                        </p>
                      ) : null}
                      {pub?.body ? (
                        <p className="whitespace-pre-line">{pub.body}</p>
                      ) : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ),
        },
        hasReviews && {
          key: "reviews",
          label: "Reviews",
          render: () => (
            <div
              role="tabpanel"
              aria-labelledby="tab-reviews"
              id="panel-reviews"
              tabIndex={0}
              className="ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 animate-fade-in mt-0"
            >
              <h3 className="font-playfair text-2xl font-bold mb-6">
                Reviews &amp; Testimonials
              </h3>
              <div className="space-y-6">
                {reviews.map((rev) => {
                  const rank = Math.max(0, Math.min(5, Number(rev?.rank ?? 0)));
                  const full = Math.floor(rank);
                  const half = rank - full >= 0.5 && full < 5;
                  const empty = 5 - full - (half ? 1 : 0);

                  return (
                    <div key={rev?.id} className="bg-gray-50 p-6 rounded-lg">
                      <div className="flex items-center mb-4">
                        <div className="star-rating flex items-center">
                          {Array.from({ length: full }, (_, i) => (
                            <Star
                              key={`${rev?.id || Math.random()}-full-${i}`}
                              size={20}
                              fill="currentColor"
                            />
                          ))}
                          {half ? (
                            <StarHalf size={16} fill="currentColor" />
                          ) : null}
                          {Array.from({ length: empty }, (_, i) => (
                            <Star
                              key={`${rev?.id || Math.random()}-empty-${i}`}
                              size={20}
                              fill="none"
                            />
                          ))}
                        </div>
                        {rev?.name ? (
                          <span className="ml-2 font-medium">{rev.name}</span>
                        ) : null}
                      </div>
                      {rev?.body ? (
                        <p className="italic whitespace-pre-line">{rev.body}</p>
                      ) : null}
                    </div>
                  );
                })}
              </div>
            </div>
          ),
        },
      ].filter(Boolean) as {
        key: string;
        label: string;
        render: () => JSX.Element;
      }[],
    [
      hasBiography,
      biography,
      hasExhibitions,
      groupedExhibitions,
      hasArtistStatement,
      artistStatement,
      hasPublications,
      sortedPublications,
      hasReviews,
      reviews,
    ]
  );

  const [activeKey, setActiveKey] = useState<string | undefined>(tabs[0]?.key);

  if (tabs.length === 0) return null;

  const activeTab = tabs.find((t) => t.key === activeKey) || tabs[0];

  if (!activeTab) return null;

  const buttonClass = (isActive: boolean) =>
    `inline-flex items-center justify-center whitespace-nowrap rounded-sm text-sm font-medium ring-offset-background transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-background data-[state=active]:text-foreground data-[state=active]:shadow-sm tab-underline px-1 py-2${
      isActive ? " active" : ""
    }`;

  return (
    <section className="py-16 bg-artist-offwhite">
      <div className="container mx-auto px-4">
        <div dir="ltr" data-orientation="horizontal" className="w-full">
          <div
            role="tablist"
            aria-orientation="horizontal"
            className="h-10 items-center rounded-md text-muted-foreground w-full flex overflow-x-auto space-x-2 sm:space-x-4 md:space-x-8 justify-start md:justify-center p-0 bg-transparent mb-8"
            tabIndex={0}
            data-orientation="horizontal"
            style={{ outline: "none" }}
          >
            {tabs.map((t) => (
              <button
                key={t.key}
                type="button"
                role="tab"
                aria-selected={activeTab.key === t.key}
                aria-controls={`panel-${t.key}`}
                id={`tab-${t.key}`}
                className={buttonClass(activeTab.key === t.key)}
                tabIndex={-1}
                data-orientation="horizontal"
                onClick={() => setActiveKey(t.key)}
              >
                {t.label}
              </button>
            ))}
          </div>
          <div className="bg-white p-6 md:p-8 rounded-lg shadow-sm">
            {activeTab.render()}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SignatureProfileTabs;
